import React from 'react';
import { List } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import { TipAlert } from '../common/TipAlert';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import CommonContext, { ApiRoutes } from '../Common';
import {
    FormCheckbox, onFieldChange, toasty,
} from '../common/forms/FormElements';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { Block, BlockBorder } from '../common/Block';
import { SaveButton } from '../common/forms/SaveButton';

const CreateNewProjectID = -1;

export class MergeProjects extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            loading: false,
            saving: false,

            // Destination
            destinationContract: null,
            destinationProject: null,
            destinationProjectOptions: [],
            showDeletedDestinationProjects: false,

            // Source
            sourceContract: null,
            sourceProject: null,
            sourceProjectOptions: [],

            // Job selection
            movableJobs: [],
            loadingJobs: false,

            isValidated: false,
        };

        this.onChange = this.onChange.bind(this);
    }

    onChange = onFieldChange;

    onSubmit = async (e) => {
        if (e) e.preventDefault();

        const {
            destinationProject,
            sourceProject,
            sourceContract,
            destinationContract,
        } = this.state;

        if (
            sourceContract?.value === destinationContract?.value
            && sourceProject?.value === destinationProject?.value
        ) {
            this.setState({ isValidated: true });
            toasty.error('Source and destination projects cannot be the same project.');
            return;
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.adminTool.mergeProjects(), {
                sourceProjectId: sourceProject.value,
                destinationProjectId: destinationProject.value,
                destinationContractId: destinationContract.value,
            });

            this.setState({
                destinationContract: null,
                destinationProject: null,
                destinationProjectOptions: [],
                sourceContract: null,
                sourceProject: null,
                sourceProjectOptions: [],
                movableJobs: [],
            });

            toasty.success('Merge Projects Completed. Please check execution history.');

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } catch (err) {
            console.error(err);
            toasty.error(err?.handledError?.message ?? 'Merge Projects Failed. Please check execution history.');

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    loadContractOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.contractsSearch(inputValue, 'true'))
            .then((data) => callback(data));
    }, 250);

    onDestinationContractChanged = async (selectedOption) => {
        const {
            showDeletedDestinationProjects,
            sourceContract,
            sourceProject,
        } = this.state;
        const destinationProjectOptions = selectedOption
            ? await httpClient.js(ApiRoutes.typeAheads.openProjectsByContract(selectedOption.value, showDeletedDestinationProjects))
            : [];

        const nextDestinationProjectOptions = (
            sourceContract?.value === selectedOption?.value
                ? destinationProjectOptions.filter((project) => project.value !== sourceProject?.value)
                : destinationProjectOptions
        );

        this.setState({
            destinationContract: selectedOption,
            destinationProject: null,
            destinationProjectOptions: nextDestinationProjectOptions,
        });
    };

    onToggleDeletedDestinationProjects = async () => {
        const { destinationContract, showDeletedDestinationProjects } = this.state;
        const nextShowDeleted = !showDeletedDestinationProjects;

        let destinationProjectOptions = [];
        if (destinationContract) {
            destinationProjectOptions = await httpClient.js(
                ApiRoutes.typeAheads.openProjectsByContract(destinationContract.value, nextShowDeleted),
            );
        }

        this.setState({
            showDeletedDestinationProjects: nextShowDeleted,
            destinationProject: null,
            destinationProjectOptions,
        });
    };

    onDestinationProjectChanged = (selectedOption) => {
        this.setState({ destinationProject: selectedOption });
    };

    onSourceContractChanged = async (selectedOption) => {
        const {
            destinationContract,
            destinationProject,
        } = this.state;
        const sourceProjectOptions = selectedOption
            ? await httpClient.js(ApiRoutes.typeAheads.openProjectsByContract(selectedOption.value))
            : [];

        const nextSourceProjectOptions = (
            destinationContract?.value === selectedOption?.value
                ? sourceProjectOptions.filter((project) => project.value !== destinationProject?.value)
                : sourceProjectOptions
        );

        this.setState({
            sourceContract: selectedOption,
            sourceProject: null,
            sourceProjectOptions: nextSourceProjectOptions,
            movableJobs: [],
        });
    };

    onSourceProjectChanged = async (selectedOption) => {
        this.setState({
            sourceProject: selectedOption, movableJobs: [], loadingJobs: true,
        });

        if (selectedOption) {
            try {
                const jobs = await httpClient.js(ApiRoutes.adminTool.movableJobs(selectedOption.value));
                this.setState({ movableJobs: jobs ?? [], loadingJobs: false });
            } catch {
                this.setState({ loadingJobs: false });
            }
        } else {
            this.setState({ loadingJobs: false });
        }
    };

    render() {
        const {
            loading,
            saving,
            destinationContract,
            destinationProject,
            destinationProjectOptions,
            showDeletedDestinationProjects,
            sourceContract,
            sourceProject,
            sourceProjectOptions,
            movableJobs,
            loadingJobs,
        } = this.state;

        if (loading) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label="Loading form..."
                    />
                </div>
            );
        }

        if (!(this.context ?? {}).user) return null;

        const sameContractSelected = sourceContract?.value === destinationContract?.value;

        const sourceProjectOptionsFiltered = sameContractSelected
            ? (sourceProjectOptions ?? []).filter((project) => project.value !== destinationProject?.value)
            : (sourceProjectOptions ?? []);

        let destinationProjectOptionsFiltered = sameContractSelected
            ? (destinationProjectOptions ?? []).filter((project) => project.value !== sourceProject?.value)
            : (destinationProjectOptions ?? []);

        destinationProjectOptionsFiltered = [
            { value: CreateNewProjectID, label: '→ Move This Project' },
            ...destinationProjectOptionsFiltered,
        ];

        return (
            <form onSubmit={this.onSubmit}>
                <Block padding="medium">
                    <div className="row">
                        {/* Source */}
                        <div className="col-md-6">
                            <h6 className="font-weight-bold mb-3">Source</h6>
                            <ValidatedSelect
                                name="sourceContract"
                                component={AsyncSelect}
                                id="sourceContract"
                                label="Source Contract"
                                required
                                error="Source contract is required"
                                placeholder="Type Contract Number Here"
                                loadOptions={this.loadContractOptions}
                                value={sourceContract}
                                onChange={this.onSourceContractChanged}
                                cacheOptions
                            />
                            <ValidatedSelect
                                id="projectSource"
                                name="projectSource"
                                label="Source Project"
                                options={sourceProjectOptionsFiltered}
                                value={sourceProject}
                                onChange={this.onSourceProjectChanged}
                                isDisabled={!sourceContract}
                                error="Source project is required."
                                required
                            />
                        </div>
                        {/* Destination */}
                        <div className="col-md-6">
                            <h6 className="font-weight-bold mb-3">Destination</h6>
                            <ValidatedSelect
                                name="destinationContract"
                                component={AsyncSelect}
                                id="destinationContract"
                                label="Destination Contract"
                                required
                                error="Destination contract is required"
                                placeholder="Type Contract Number Here"
                                loadOptions={this.loadContractOptions}
                                value={destinationContract}
                                onChange={this.onDestinationContractChanged}
                                cacheOptions
                            />

                            <ValidatedSelect
                                id="projectDestination"
                                name="projectDestination"
                                label="Destination Project"
                                options={destinationProjectOptionsFiltered}
                                value={destinationProject}
                                onChange={this.onDestinationProjectChanged}
                                isDisabled={!destinationContract}
                                error="Destination project is required."
                                required
                            />

                            <FormCheckbox
                                id="show-deleted-destination-projects"
                                labelText="Show deleted destination projects"
                                checked={showDeletedDestinationProjects}
                                onChange={this.onToggleDeletedDestinationProjects}
                                className="mt-2"
                            />
                        </div>
                    </div>

                    {/* Job selection */}
                    {loadingJobs && (
                        <div className="mt-3 text-center">
                            <CustomCircularProgress
                                color="secondary"
                                variant="indeterminate"
                                label="Checking jobs..."
                            />
                        </div>
                    )}

                    {!loadingJobs && sourceProject && movableJobs.length === 0 && (
                        <div className="mt-3 alert alert-warning">
                            No movable jobs found for this project. All jobs may have already been invoiced.
                        </div>
                    )}

                    <TipAlert
                        containerClassName="mb-3 mt-3"
                        tipText="Note"
                        label="Closed projects and jobs within invoiced periods on either the source or destination project cannot be merged. Source and destination can be on different contracts."
                    />

                    <BlockBorder />

                    <div className="d-flex justify-content-center">
                        <SaveButton
                            saving={saving}
                            label="Submit"
                            disabled={!destinationProject || !sourceProject || loadingJobs || movableJobs.length === 0}
                        />
                    </div>
                </Block>
            </form>
        );
    }
}

export const MergeProjectHistoryForm = ({ record }) => (
    <div>
        <List type="unstyled">
            {record.input.source_projects && (
                <li>
                    <strong>From Projects:</strong>
                    {' '}
                    {record.input.source_projects.join(', ')}
                </li>
            )}
            {record.input.destination_project && (
                <li>
                    <strong>To Project:</strong>
                    {' '}
                    {record.input.destination_project}
                </li>
            )}
            {record.output.merged_project && (
                <li>
                    <strong>Project Merged:</strong>
                    {' '}
                    {record.output.merged_project.join(', ')}
                </li>
            )}
            {Array.isArray(record.output.failed_project) && record.output.failed_project.length > 0 && (
                <li>
                    <strong>Project Merge Failed:</strong>
                    {' '}
                    {record.output.failed_project.join(', ')}
                </li>
            )}
        </List>
    </div>
);